import { useEffect, useRef, useState, useCallback } from "react";
import type { RealtimeChannel } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase";

export type PresenceStatus = "online" | "idle" | "offline";

interface PresenceMeta {
  user_id: string;
  status: "online" | "idle";
  updated_at: number;
  presence_ref: string;
}

const IDLE_AFTER_MS = 5 * 60 * 1000;
const CHANNEL = "presence:global";

/**
 * VIB-535: global presence via Supabase Realtime Presence.
 *
 * Én delt kanal på tvers av hele appen — tracker aktiv bruker og lar andre
 * komponenter lese online/idle-status for vilkårlig user-id. Offline betyr
 * at brukeren ikke har en aktiv tilkobling (ikke i presenceState).
 *
 * Idle: 5 min uten mousemove/keydown. Auto-recover til online ved
 * neste aktivitet. Unsubscribe på unmount.
 */
export function usePresence(userId: string | null | undefined) {
  const [presence, setPresence] = useState<Map<string, PresenceStatus>>(
    () => new Map(),
  );
  const channelRef = useRef<RealtimeChannel | null>(null);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastStatusRef = useRef<"online" | "idle">("online");

  const track = useCallback(
    (status: "online" | "idle") => {
      const ch = channelRef.current;
      if (!ch || !userId) return;
      lastStatusRef.current = status;
      ch.track({
        user_id: userId,
        status,
        updated_at: Date.now(),
      });
    },
    [userId],
  );

  const resetIdleTimer = useCallback(() => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    if (lastStatusRef.current === "idle") track("online");
    idleTimerRef.current = setTimeout(() => track("idle"), IDLE_AFTER_MS);
  }, [track]);

  useEffect(() => {
    if (!userId) return;

    const channel = supabase.channel(CHANNEL, {
      config: { presence: { key: userId } },
    });

    channel.on("presence", { event: "sync" }, () => {
      const state = channel.presenceState() as Record<string, PresenceMeta[]>;
      const next = new Map<string, PresenceStatus>();
      for (const [uid, metas] of Object.entries(state)) {
        // Én bruker kan ha flere faner/enheter; online > idle hvis mix
        const latest = metas[metas.length - 1];
        const anyOnline = metas.some((m) => m.status === "online");
        next.set(uid, anyOnline ? "online" : latest?.status ?? "online");
      }
      setPresence(next);
    });

    channel.subscribe(async (status) => {
      if (status === "SUBSCRIBED") {
        await channel.track({
          user_id: userId,
          status: "online",
          updated_at: Date.now(),
        });
        lastStatusRef.current = "online";
      }
    });

    channelRef.current = channel;

    // Aktivitetslyttere for idle-deteksjon
    const activityEvents = ["mousemove", "keydown", "touchstart"] as const;
    for (const ev of activityEvents) {
      window.addEventListener(ev, resetIdleTimer, { passive: true });
    }
    resetIdleTimer();

    return () => {
      for (const ev of activityEvents) {
        window.removeEventListener(ev, resetIdleTimer);
      }
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      supabase.removeChannel(channel);
      channelRef.current = null;
    };
  }, [userId, resetIdleTimer]);

  return presence;
}

/**
 * Henter status for én enkelt bruker fra presence-mappet.
 * Standardverdi = "offline" hvis brukeren ikke er i mappet.
 */
export function getPresenceStatus(
  map: Map<string, PresenceStatus>,
  userId: string | null | undefined,
): PresenceStatus {
  if (!userId) return "offline";
  return map.get(userId) ?? "offline";
}
